import React, { useState } from 'react';
import { IpaType } from '../../phonemes/ipa';
import { getPhoibleUrl } from '../../world/languageToPhoibleId';
import { allFilterProps, allSortProps, FilterProp, SortProp, TrackLanguageFeatures } from '../../world/trackLanguageFeatures';
import { IpaChart } from '../chart/IpaChart';
import { speakSample, hasSample } from '../chart/speakSample';
import { getLanguageTrackers } from '../LanguageTrackers';
import { ChoiceList } from '../widgets/ChoiceList';
import { loadWorldData } from '../../loader/loadWorldData';

interface ReferenceChartProps {
  readonly customIpa: Set<IpaType>;   // chartIpa
  readonly customDiphthongs: string[];
  readonly onSetSegments: (segments: string) => void; // cleanSegments
}

/**
 * Show an IPA chart, either hilighting custom phonemes or from a selected language
 */
export function ReferenceChart(props: ReferenceChartProps) {
  const [language, setLanguage] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [lenguaSegments, setLenguaSegments] = useState('');
  const [lenguaDiphthongs, setLenguaDiphthongs] = useState<string[]>([]);
  const [trackers, setTrackers] = useState<TrackLanguageFeatures | null>(null);
  const [languageList, setLanguageList] = useState(['loading...']);
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [init, setInit] = useState(false);

  if (!init) {
    setInit(true);
    loadWorldData().then(() => {
      setTrackers(getLanguageTrackers());
      setLanguageList([""].concat(getLanguageTrackers().sortedByName()));
    });
  }

  const phoibleId = getPhoibleUrl(language);
  const link = phoibleId === null ? "reference link" : <a href={phoibleId}>reference link</a>;

  function pickLanguage(language: string) {
    if (trackers) {
      const stats = trackers.lookupStats(language);
      setLenguaSegments(stats.segments);
      setLenguaDiphthongs(stats.vowelClusters);
      setLanguage(language);
    }
  }

  function changeSortBy(prop: string) {
    setSortBy(prop);
    makeList(prop, filterBy);
  }
  function changeFilterBy(prop: string) {
    setFilterBy(prop);
    makeList(sortBy, prop);
  }
  /** sort & filter the list of languages */
  function makeList(sortProp: string, filterProp: string) {
    if (trackers) {
      const sorted = trackers.sortedByCount(sortProp as SortProp);
      const filtered = trackers.filteredByProp(filterProp as FilterProp, sorted);
      setLanguageList([""].concat(filtered));
      setLanguage("");
    }
  }

  function background(ipa: IpaType): string | undefined {
    if (showCustom) {
      // show IPA for the custom segments
      const inSegments = props.customIpa.has(ipa);
      return inSegments ? 'rgb(255,200,200)' : undefined;
    } else if (trackers) {
      // show IPA for the selected language
      const inSegments = trackers.lookupStats(language).chartIpa.includes(ipa);
      return inSegments ? 'rgb(200,255,200)' : undefined;
    }
  }

  return (<>
    language:&nbsp;
    <ChoiceList items={languageList} selectedItem={language} onSelect={pickLanguage} />
    &nbsp;&nbsp;
    {link}
    <br /><br />
    sort by: <ChoiceList items={allSortProps} selectedItem={sortBy} onSelect={changeSortBy} />
    &nbsp;&nbsp;
    filter to languages with: <ChoiceList items={allFilterProps} selectedItem={filterBy} onSelect={changeFilterBy} />
    <br />
    <br />
    segments:&nbsp;
    <textarea cols={80} rows={5} spellCheck={false} value={lenguaSegments} onChange={(event) => setLenguaSegments(event.target.value)} />
    <br />
    <button onClick={() => props.onSetSegments(lenguaSegments)} disabled={lenguaSegments.length === 0}>^use segments^</button>
    <br />

    <input type="checkbox" id="showCustom" onChange={(e) => setShowCustom(e.target.checked)} />
    <label htmlFor="showCustom">show custom</label>

    <IpaChart diphthongs={showCustom ? props.customDiphthongs : lenguaDiphthongs}
      lookup={(ipa: IpaType) => ipa} combinedConsonants={true} background={background}
      clicked={(ipa) => { speakSample(ipa) }} canClick={(ipa) => hasSample(ipa)} />
  </>);
}
